import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { RefData } from '../../types';
import { secureRefSplitApi } from './ref-api';

const continentsApi = secureRefSplitApi.injectEndpoints({
  endpoints: (build) => ({
    fetchContinents: build.query<RefData[], void>({
      query: () => ({ url: '/continents', method: 'GET' }),
      keepUnusedDataFor: 60 * 60 * 24 // 24 hours
    }),
    fetchContinentCountries: build.query<
      RefData[],
      { continentId: string | number }
    >({
      query: ({ continentId }) => ({
        url: `/continent/${continentId}/countries`,
        method: 'GET'
      }),
      keepUnusedDataFor: 60 * 60 * 24 // 24 hours
    }),
    fetchAllCountriesWithContinents: build.query<
      {
        label: string;
        value: string;
        continentId: string;
        continentLabel: string;
      }[],
      void
    >({
      queryFn: async (_, { dispatch }, _extraOptions) => {
        const continents: RefData[] = await dispatch(
          continentsApi.endpoints.fetchContinents.initiate()
        ).unwrap();

        const promises = continents.map(async (continent) => {
          const countries: RefData[] = await dispatch(
            continentsApi.endpoints.fetchContinentCountries.initiate({
              continentId: continent.value
            })
          ).unwrap();

          return countries.map((country) => ({
            ...country,
            continentId: continent.value,
            continentLabel: continent.label
          }));
        });

        const result = (await Promise.all(promises))
          .reduce<
            {
              label: string;
              value: string;
              continentId: string;
              continentLabel: string;
            }[]
          >((acc, curr) => {
            acc.push(...curr);
            return acc;
          }, [])
          .sort((a, b) => a.label.localeCompare(b.label));

        return { data: result, error: undefined };
      },
      keepUnusedDataFor: 60 * 60 * 24 // 24 hours
    })
  }),
  overrideExisting: true
});

export const {
  useFetchContinentsQuery,
  useLazyFetchContinentsQuery,
  useLazyFetchContinentCountriesQuery,
  useFetchContinentCountriesQuery,
  useFetchAllCountriesWithContinentsQuery,
  useLazyFetchAllCountriesWithContinentsQuery
} = continentsApi;
